import React, { ReactNode, useState } from "react"
import { useQueryClient } from "react-query"
import {
  Box, Flex, Input, Stack, Text,
} from "@chakra-ui/react"
import Button from "components/Buttons/Button"
import Icon from "components/elements/Icon"
import LanguageTemplatesIcon from "components/elements/LanguageTemplatesIcon"
import FormLanguageModal from "modals/FormLanguageModal"
import { useCurrentEvent } from "queries/events"
import { useLanguageTemplates } from "queries/languageTemplates"
import { useAccountConfiguration } from "queries/users"
import { patchForm } from "api/forms"
import { refetchForms, updateForm } from "context/actions"
import { successToast } from "utilities/toasts"
import { Form, Translations } from "sharedTypes"
import useResolvedTranslations from "pages/EventShow/Settings/LanguageTemplates/useResolvedTranslations"

type Props = {
  eventId: string
  /** When given, distinguishes this form's own override from the event's default, and
   * offers changing it directly via a "Change Form Language" button. Omit for a plain
   * event-level reference (e.g. Map Link, Kiosk) — those pass `description` instead, since
   * they have no button/action of their own, just PM-authored explanatory copy. */
  form?: Form
  /** When given, also shows a default-vs-resolved preview for these fields (e.g. every Kiosk
   * on-screen text string). Accepts one field or several. */
  field?: { key: keyof Translations } | { key: keyof Translations }[]
  /** Explanatory copy, given the resolved template's name so the caller can bold it inline. */
  description?: (resolvedName: string) => ReactNode
}

const LanguageTemplateReference = ({
  eventId, form, field, description,
}: Props) => {
  const queryClient = useQueryClient()
  const { data: event } = useCurrentEvent()
  const { data: templates = [] } = useLanguageTemplates(eventId)
  const { accountConfiguration } = useAccountConfiguration()
  const translations = useResolvedTranslations(form?.languageTemplateId)
  const [showLanguageModal, setShowLanguageModal] = useState(false)

  const fields = field ? [field].flat() : []
  const resolvedId = form?.languageTemplateId ?? event.defaultLanguageTemplateId
  const template = templates.find((t) => t.id === resolvedId)
  const resolvedName = template?.name ?? "English"

  const handleSaveFormLanguage = async (languageTemplateId: string | null) => {
    if (!form) {
      return
    }

    const { data: updatedForm } = await patchForm(eventId, form.externalId, { languageTemplateId })
    refetchForms(queryClient, eventId)
    // refetchForms only invalidates the list — the Builder reads its own single-form query
    updateForm(queryClient, form.externalId, updatedForm)
    successToast({ title: "Form Language updated" })
    setShowLanguageModal(false)
  }

  return (
    <Stack spacing={3}>
      {form && (
        <Flex justify="space-between" align="center">
          <Text fontWeight="bold">Language</Text>
          <Flex align="center" gap={1}>
            <LanguageTemplatesIcon boxSize={4} color="#7ED321" />
            <Text color="#7ED321" fontWeight="semibold">{resolvedName}</Text>
          </Flex>
        </Flex>
      )}
      {description && description(resolvedName)}
      {fields.length > 0 && (
        <Stack spacing={2}>
          {fields.map(({ key }) => (
            <Flex key={key}>
              <Box w="50%">
                <Input value={accountConfiguration.translations[key]} isDisabled />
              </Box>
              <Icon icon="arrow-right" size={4} w="10%" mt={3} />
              <Box w="50%">
                <Input value={translations[key]} isDisabled />
              </Box>
            </Flex>
          ))}
        </Stack>
      )}
      {form && (
        <Flex justify="center">
          <Button variant="outline" size="sm" onClick={() => setShowLanguageModal(true)}>
            Change Form Language
          </Button>
        </Flex>
      )}
      {showLanguageModal && form && (
        <FormLanguageModal
          eventId={eventId}
          currentLanguageTemplateId={form.languageTemplateId ?? null}
          onClose={() => setShowLanguageModal(false)}
          onSave={handleSaveFormLanguage}
        />
      )}
    </Stack>
  )
}

export default LanguageTemplateReference
